import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, Download, Lock, Users, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ahb_admin_token";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function LoginForm({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email: email.trim(), password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLoggedIn();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7] paper-grain px-6">
      <div className="w-full max-w-sm rounded-3xl border border-stone-200 bg-white p-8 sm:p-10 shadow-[0_20px_50px_-24px_rgba(32,28,25,0.3)]">
        <div className="flex items-center gap-2 text-[#2a7b76] mb-2">
          <Lock size={16} />
          <span className="text-xs uppercase tracking-[0.24em] font-semibold">Private area</span>
        </div>
        <h1 className="font-display text-3xl text-[#201c19] mb-6">Launch list</h1>
        <form onSubmit={submit} className="space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            data-testid="admin-email-input"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-[#201c19] placeholder:text-stone-400 focus:outline-none focus:border-[#2a7b76] focus:ring-2 focus:ring-[#2a7b76]/20 transition"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password-input"
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-[#201c19] placeholder:text-stone-400 focus:outline-none focus:border-[#2a7b76] focus:ring-2 focus:ring-[#2a7b76]/20 transition"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#201c19] text-[#fdfbf7] font-semibold px-6 py-3 hover:bg-[#2a7b76] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setSubs([]);
  }, []);

  const load = useCallback(async () => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setSubs(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Could not load subscribers.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const exportCsv = async () => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) return;
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/subscribers.csv`, {
        headers: { Authorization: `Bearer ${t}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Could not export CSV.");
    } finally {
      setExporting(false);
    }
  };

  if (!token) return <LoginForm onLoggedIn={() => setToken(sessionStorage.getItem(TOKEN_KEY))} />;

  return (
    <div className="min-h-screen bg-[#fdfbf7] paper-grain">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#2a7b76] font-semibold mb-2">Launch list</p>
            <h1 className="font-display text-4xl sm:text-5xl text-[#201c19] font-light">Subscribers</h1>
            <p data-testid="subscriber-count" className="mt-2 flex items-center gap-2 text-stone-500 text-sm">
              <Users size={15} /> {subs.length} {subs.length === 1 ? "person has" : "people have"} joined
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2.5 text-sm text-[#201c19] hover:border-[#2a7b76] hover:text-[#2a7b76] transition-colors"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={exportCsv}
              disabled={exporting || subs.length === 0}
              data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#c2751f] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#a9631a] transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2.5 text-sm text-[#201c19] hover:border-rose-400 hover:text-rose-500 transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-stone-400">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : subs.length === 0 ? (
            <div data-testid="empty-state" className="text-center py-20 text-stone-400">
              No one has joined the list yet.
            </div>
          ) : (
            <table className="w-full text-left" data-testid="subscribers-table">
              <thead>
                <tr className="border-b border-stone-200 bg-[#f5f2ea]/60">
                  <th className="px-6 py-4 text-xs uppercase tracking-wider text-stone-500 font-semibold">#</th>
                  <th className="px-6 py-4 text-xs uppercase tracking-wider text-stone-500 font-semibold">Name</th>
                  <th className="px-6 py-4 text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</th>
                  <th className="px-6 py-4 text-xs uppercase tracking-wider text-stone-500 font-semibold">Phone</th>
                  <th className="px-6 py-4 text-xs uppercase tracking-wider text-stone-500 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => (
                  <tr key={s.id} data-testid={`subscriber-row-${i}`} className="border-b border-stone-100 last:border-0 hover:bg-[#fdfbf7]">
                    <td className="px-6 py-4 text-sm text-stone-400">{i + 1}</td>
                    <td className="px-6 py-4 text-sm text-[#201c19] font-medium">{s.name || <span className="text-stone-300">—</span>}</td>
                    <td className="px-6 py-4 text-sm text-[#201c19]">{s.email}</td>
                    <td className="px-6 py-4 text-sm text-stone-500">{s.phone || <span className="text-stone-300">—</span>}</td>
                    <td className="px-6 py-4 text-sm text-stone-500">{formatDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
